// client/src/components/pages/LexiStar/Drivers/DriverDialog.jsx
//
// Create / edit a driver (FR-DRV-001 subset held by the M3 register: name,
// tag / employee number, language, mobile number, Telegram chat, preferred
// channel, active, notes). The number is normalised to E.164 as typed so the
// dispatcher sees what will be stored; the server applies the same rule.

import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { inputClasses } from '../StatusBoard/atoms';
import { createDriver, updateDriver } from './driversApi';
import { DRIVER_CHANNELS, LANGUAGES, normalisePhone } from './driverLogic';

const EMPTY = {
  name: '',
  tag: '',
  language: 'en',
  phone: '',
  telegram_chat_id: '',
  preferred_channel: '',
  active: true,
  notes: '',
};

export const errorText = (err, fallback) => {
  const data = err?.response?.data || {};
  const details = Array.isArray(data.details)
    ? data.details.map((d) => (typeof d === 'string' ? d : d.message)).filter(Boolean)
    : [];
  return (
    [data.error || data.message, ...details].filter(Boolean).join(' — ') || err?.message || fallback
  );
};

export default function DriverDialog({ isOpen, driver, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setForm(
      driver
        ? {
            name: driver.name || '',
            tag: driver.tag || '',
            language: driver.language || 'en',
            phone: driver.phone_e164 || '',
            telegram_chat_id: driver.telegram_chat_id || '',
            preferred_channel: driver.preferred_channel || '',
            active: driver.active !== false,
            notes: driver.notes || '',
          }
        : EMPTY
    );
  }, [isOpen, driver]);

  if (!isOpen) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const phoneE164 = form.phone.trim() ? normalisePhone(form.phone) : null;
  const phoneBad = !!form.phone.trim() && !phoneE164;
  const chatBad =
    !!form.telegram_chat_id.trim() && !/^-?\d{1,19}$/.test(form.telegram_chat_id.trim());
  const canSave = form.name.trim() && !phoneBad && !chatBad && !saving;

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      tag: form.tag.trim() || null,
      language: form.language,
      phone_e164: phoneE164,
      telegram_chat_id: form.telegram_chat_id.trim() || null,
      preferred_channel: form.preferred_channel || null,
      active: form.active,
      notes: form.notes.trim() || null,
    };
    try {
      const res = driver
        ? await updateDriver(driver.id, { ...payload, version: driver.version })
        : await createDriver(payload);
      onSaved?.(res?.driver || res);
      onClose();
    } catch (err) {
      setError(errorText(err, 'Saving the driver failed'));
    } finally {
      setSaving(false);
    }
  };

  const field = (id, label, input, hint) => (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-600 mb-1">
        {label}
      </label>
      {input}
      {hint}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={driver ? `Edit ${driver.name}` : 'New driver'}
        className="bg-surface rounded-2xl shadow-2xl max-w-xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">
            {driver ? `Edit ${driver.name}` : 'New driver'}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          {error && (
            <p
              role="alert"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {error}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field(
              'drv-name',
              'Name',
              <input
                id="drv-name"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                className={inputClasses}
                autoComplete="off"
              />
            )}
            {field(
              'drv-tag',
              'Tag / employee number',
              <input
                id="drv-tag"
                value={form.tag}
                onChange={(e) => set({ tag: e.target.value })}
                className={inputClasses}
                autoComplete="off"
              />
            )}
            {field(
              'drv-phone',
              'Mobile number',
              <input
                id="drv-phone"
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                className={inputClasses}
                inputMode="tel"
                placeholder="082 123 4567"
                autoComplete="off"
              />,
              <p className={`text-xs mt-1 ${phoneBad ? 'text-red-700' : 'text-slate-500'}`}>
                {phoneBad
                  ? 'Not a phone number we can message.'
                  : phoneE164
                    ? `Stored as ${phoneE164}`
                    : 'For WhatsApp and SMS.'}
              </p>
            )}
            {field(
              'drv-telegram',
              'Telegram chat id',
              <input
                id="drv-telegram"
                value={form.telegram_chat_id}
                onChange={(e) => set({ telegram_chat_id: e.target.value })}
                className={inputClasses}
                inputMode="numeric"
                autoComplete="off"
              />,
              <p className={`text-xs mt-1 ${chatBad ? 'text-red-700' : 'text-slate-500'}`}>
                {chatBad ? 'Digits only.' : 'Filled in when the driver starts the LexiStar bot.'}
              </p>
            )}
            {field(
              'drv-language',
              'Language',
              <ThemedSelect
                id="drv-language"
                value={form.language}
                onChange={(e) => set({ language: e.target.value })}
                className={inputClasses}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </ThemedSelect>
            )}
            {field(
              'drv-channel',
              'Preferred channel',
              <ThemedSelect
                id="drv-channel"
                value={form.preferred_channel}
                onChange={(e) => set({ preferred_channel: e.target.value })}
                className={inputClasses}
                isClearable
              >
                <option value="">Tenant default order</option>
                {DRIVER_CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </ThemedSelect>
            )}
          </div>
          {field(
            'drv-notes',
            'Notes',
            <textarea
              id="drv-notes"
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              rows={3}
              className={inputClasses}
            />
          )}
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set({ active: e.target.checked })}
              className="rounded border-slate-300"
            />
            Active (can be allocated and messaged)
          </label>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : driver ? 'Save changes' : 'Add driver'}
          </button>
        </div>
      </div>
    </div>
  );
}
